import React, { useState } from 'react';
import {
  User, MapPin, Package,
  Check, Printer, Eye,
  RefreshCw, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrderCreateMutation, useServiceAllListData } from "../../apis/orderApis";
import { useFetchCountriesAllData, useFetchCountryCitiesAllData, useDistrictFetchData } from "../../apis/locationApis";
import { FaSpinner, FaMapMarkerAlt, FaExternalLinkAlt } from "react-icons/fa";
import shortAddresses from "../../riyadh_short_addresses.json";
import SelectInput from "../../components/Select";
import { ORDER_SHIPMENT_TYPE_OPTIONS, PAYMENT_TYPE_OPTIONS, AddressOption } from "../../types/types";
import { useQueryClient } from "@tanstack/react-query";

export default function OrderCreateOutlet() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('create');
  const [ordersDirectory, setOrdersDirectory] = useState<any[]>([]);

  const [toast, setToast] = useState<{ message: string, type: string } | null>(null);

  const triggerToast = (message: string, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const { data: servicesData } = useServiceAllListData();
  const { data: countries = [] } = useFetchCountriesAllData();

  const [sender, setSender] = useState({
    name: '',
    mobile: '',
    dialCode: '966',
    email: '',
    iqamaNumber: ''
  });

  const [receiver, setReceiver] = useState({
    name: '',
    country: 'Saudi Arabia',
    mobile: '',
    dialCode: '966',
    iqamaNumber: '',
    email: '',
    shortAddress: '',
    addressType: 'short_address',
    city: '',
    district: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    address: ''
  });

  const { mutate: createOrder, isPending } = useOrderCreateMutation();
  const [citySearchRef, setCitySearchRef] = useState<string>("");
  const [districtSearchRef, setDistrictSearchRef] = useState<string>("");

  const { data: cities = [], isPending: isCitiesFetchPending, refetch: fetchReceiverCities } = useFetchCountryCitiesAllData(
    receiver?.country,
    citySearchRef,
    (receiver?.addressType === 'short_address' ? 'short' : receiver?.addressType) as AddressOption
  );

  const { data: districts = [], isPending: isDistrictFetchPending, refetch: fetchReceiverDistricts } = useDistrictFetchData(
    receiver?.city,
    districtSearchRef,
    (receiver?.addressType === 'short_address' ? 'short' : receiver?.addressType) as AddressOption
  );

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setCitySearchRef(query);
      fetchReceiverCities();
    }
  };

  const handleDistrictSearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setDistrictSearchRef(query);
      fetchReceiverDistricts();
    }
  };

  const [shipment, setShipment] = useState({
    reference: '',
    shipmentType: ORDER_SHIPMENT_TYPE_OPTIONS[0]?.value || 'PARCEL',
    service: '',
    pieces: '' as any,
    weight: '',
    cost: 0.00,
    description: '',
    paymentMethod: PAYMENT_TYPE_OPTIONS[0]?.value || 'CC',
    collectedAmount: ''
  });

  const [selectedOrderPreview, setSelectedOrderPreview] = useState<any>(null);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [pendingDistrictUpdate, setPendingDistrictUpdate] = useState<string | null>(null);

  const normalizeAddressData = React.useCallback((addr: any) => {
    const coordsStr = addr?.googleMapsLink?.split("/place/")[1];
    const coords = coordsStr?.split(",");
    const latitude = coords?.[0] ? parseFloat(coords[0]) : 0;
    const longitude = coords?.[1] ? parseFloat(coords[1]) : 0;
    return {
      shortAddress: addr.shortAddress,
      nationalAddress: {
        buildingNumber: addr.nationalAddress?.buildingNumber || addr.buildingNumber || "",
        street: addr.nationalAddress?.street || addr.street || "",
        secondaryNumber: addr.nationalAddress?.secondaryNumber || addr.secondaryNumber || "",
        district: addr.nationalAddress?.district || addr.district || "",
        postalCode: addr.nationalAddress?.postalCode || addr.postalCode || "",
        city: addr.nationalAddress?.city || addr.city || "",
      },
      location: addr.location || {
        latitude,
        longitude,
        googleMapsLink: addr.googleMapsLink || "",
      },
      googleMapsLink: addr.location?.googleMapsLink || addr.googleMapsLink || "",
    };
  }, []);

  const getAddressSuggestions = React.useCallback(
    (code: string) => {
      if (!code || code.length < 3) {
        setSuggestions([]);
        return;
      }
      const normalizedList = shortAddresses.map(normalizeAddressData);
      const filtered = normalizedList.filter((addr) =>
        addr.shortAddress.toLowerCase().includes(code.toLowerCase()),
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    },
    [normalizeAddressData],
  );

  const convertCityToTitleCase = React.useCallback((address: any) => {
    if (address.city && typeof address.city === "string") {
      address.city = address.city
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return address;
  }, []);

  const updateFormFields = React.useCallback(
    (addressData: any) => {
      const address = convertCityToTitleCase(addressData);
      setSelectedAddress(address);

      const fullAddress = [
        address.nationalAddress.buildingNumber,
        address.nationalAddress.street,
        address.nationalAddress.district,
        address.nationalAddress.city,
        address.nationalAddress.postalCode,
        "Saudi Arabia",
      ]
        .filter(Boolean)
        .join(", ");

      let matchedCityId = "";
      if (address.nationalAddress.city && Array.isArray(cities)) {
        const cityData = cities.find(
          (c: any) =>
            c.name.toLowerCase() === address.nationalAddress.city.toLowerCase(),
        );
        if (cityData) {
          matchedCityId = cityData.id;
          if (address.nationalAddress.district) {
            setPendingDistrictUpdate(address.nationalAddress.district);
          }
        }
      }

      setReceiver((prev) => ({
        ...prev,
        shortAddress: address.shortAddress,
        address: fullAddress,
        zipCode: address.nationalAddress.postalCode || "",
        latitude: address.location?.latitude?.toString() || "",
        longitude: address.location?.longitude?.toString() || "",
        city: matchedCityId || prev.city,
        district: matchedCityId ? "" : prev.district,
      }));

      setShowSuggestions(false);
    },
    [cities, convertCityToTitleCase]
  );

  React.useEffect(() => {
    if (receiver.addressType === 'short_address') {
      getAddressSuggestions(receiver.shortAddress);
    }
  }, [receiver.shortAddress, receiver.addressType, getAddressSuggestions]);

  React.useEffect(() => {
    if (pendingDistrictUpdate && Array.isArray(districts) && districts.length > 0) {
      const districtData = districts.find(
        (d: any) => d.name.toLowerCase() === pendingDistrictUpdate.toLowerCase()
      );
      if (districtData) {
        setReceiver((prev) => ({ ...prev, district: districtData.id }));
      }
      setPendingDistrictUpdate(null);
    }
  }, [districts, pendingDistrictUpdate]);


  const handlePreviewAndCollect = (e: React.FormEvent) => {
    e.preventDefault();

    if (sender.iqamaNumber.length !== 10) {
      triggerToast('Sender Iqama Number must be exactly 10 digits', 'error');
      return;
    }
    if (sender.mobile.length < 9 || sender.mobile.length > 12) {
      triggerToast('Sender Mobile must be 9-12 digits', 'error');
      return;
    }
    if (receiver.mobile.length < 9 || receiver.mobile.length > 12) {
      triggerToast('Receiver Mobile must be 9-12 digits', 'error');
      return;
    }
    if (!receiver.iqamaNumber || receiver.iqamaNumber.length !== 10) {
      triggerToast('Receiver Iqama Number is required and must be exactly 10 digits', 'error');
      return;
    }
    if (!shipment.pieces || shipment.pieces < 1) {
      triggerToast('Number of pieces is required', 'error');
      return;
    }

    if (!sender.name || !sender.mobile || !sender.iqamaNumber) {
      triggerToast('Sender fields (Name, Mobile, Iqama) are required', 'error');
      return;
    }
    if (receiver.addressType === 'short_address' && !receiver.shortAddress) {
      triggerToast('Short Address is required', 'error');
      return;
    }
    if (receiver.addressType === 'manual' && (!receiver.city || !receiver.district || !receiver.address)) {
      triggerToast('City, District, and Address are required for manual entry', 'error');
      return;
    }
    if (!shipment.reference || !shipment.weight) {
      triggerToast('Shipment Reference and Weight are required', 'error');
      return;
    }
    if (parseFloat(shipment.cost as any) < 0) {
      triggerToast('Base Rate cannot be negative', 'error');
      return;
    }

    const payload = {
      type: "outlet",
      sender: {
        name: sender.name,
        mobile: `+${sender.dialCode}${sender.mobile}`,
        dialCode: sender.dialCode,
        email: sender.email || "sender@example.com",
        iqamaNo: sender.iqamaNumber,
        country: Array.isArray(countries) ? (countries.find((c: any) => c.name === 'Saudi Arabia')?.id || null) : null,
        city: null,
        district: null,
        address: "",
        nationalAddress: sender.iqamaNumber,
        zipCode: "",
      },
      receiver: {
        name: receiver.name,
        country: receiver.country === 'Saudi Arabia' ? (Array.isArray(countries) ? (countries.find((c: any) => c.name === 'Saudi Arabia')?.id || null) : null) : (receiver.country || null),
        city: receiver.city || null,
        district: receiver.district || null,
        mobile: `+${receiver.dialCode}${receiver.mobile}`,
        dialCode: receiver.dialCode,
        email: receiver.email || "receiver@example.com",
        iqamaNo: receiver.iqamaNumber,
        address: receiver.address || receiver.shortAddress,
        shortAddress: receiver.addressType === 'short_address' ? receiver.shortAddress : "",
        nationalAddress: receiver.addressType === 'short_address' ? receiver.shortAddress : "",
        zipCode: receiver.zipCode || "",
        latitude: receiver.latitude || null,
        longitude: receiver.longitude || null,
      },
      shipment: {
        referenceNumber: shipment.reference,
        shipmentType: shipment.shipmentType,
        totalWeight: parseFloat(shipment.weight),
        numberOfPieces: shipment.pieces,
        serviceType: shipment.service,
        description: shipment.description || "",
        paymentType: shipment.paymentMethod,
        shipmentCost: parseFloat(shipment.cost as any) || 0,
      }
    };

    createOrder(payload, {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries();
        triggerToast("Order Created successfully!");
        handleResetForm();
        const newAwb = data?.data?.awbNumber || "AWB-2026-" + Math.floor(1000 + Math.random() * 9000);
        const newOrder = {
          id: newAwb,
          date: new Date().toISOString().split('T')[0],
          sender: { ...sender, iqama: sender.iqamaNumber },
          receiver: { ...receiver, iqama: receiver.iqamaNumber },
          shipment: {
            ...shipment,
            weight: parseFloat(shipment.weight),
            cost: parseFloat(shipment.cost.toString())
          }
        };

        setOrdersDirectory([newOrder, ...ordersDirectory]);
        setSelectedOrderPreview(newOrder);
      },
      onError: (error: any) => {
        triggerToast(error?.response?.data?.message || error?.message || "Failed to create order", 'error');
      }
    });
  };

  const handleResetForm = () => {
    setSender({ name: '', mobile: '', dialCode: '966', email: '', iqamaNumber: '' });
    setSelectedAddress(null);
    setReceiver({ name: '', country: 'Saudi Arabia', mobile: '', dialCode: '966', iqamaNumber: '', email: '', shortAddress: '', addressType: 'short_address', city: '', district: '', zipCode: '', latitude: '', longitude: '', address: '' });
    setShipment({ reference: '', shipmentType: ORDER_SHIPMENT_TYPE_OPTIONS[0]?.value || 'PARCEL', service: '', pieces: '' as any, weight: '', cost: 0.00, description: '', paymentMethod: PAYMENT_TYPE_OPTIONS[0]?.value || 'CC', collectedAmount: '' });
  };

  const baseCost = parseFloat(shipment.cost as any) || 0;

  return (
    <div className="min-h-[calc(100vh-100px)] text-[#404040] font-sans">

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white transition-all duration-300 ${toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
          }`}>
          <Check className="h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto p-4 lg:p-6 space-y-6">

        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 bg-white border border-[#D8D8D8] rounded-full hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Create New Order</h1>
        </div>        {activeTab === 'create' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

            <form onSubmit={handlePreviewAndCollect} className="xl:col-span-2 space-y-6">

              <div className="bg-[#fff] border border-[#D8D8D8] rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-[#FBD037]/20 text-yellow-700 rounded-full">
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-[#828282] font-semibold text-[12px] uppercase tracking-wide">SENDER DETAILS</h2>
                      <p className="text-xs text-gray-500">Shipper verified credentials</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Full Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. Ahmed Al-Sudais"
                      required
                      value={sender.name}
                      onChange={(e) => setSender({ ...sender, name: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Mobile Number *</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 rounded-l-full border border-r-0 border-[#D8D8D8] bg-gray-50 text-gray-500 text-sm">
                        +{sender.dialCode}
                      </span>
                      <input
                        type="text"
                        placeholder="50 123 4567"
                        required
                        value={sender.mobile}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val.length <= 12) setSender({ ...sender, mobile: val });
                        }}
                        className="w-full rounded-r-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Email Address</label>
                    <input
                      type="email"
                      placeholder="ahmed.s@example.com"
                      value={sender.email}
                      onChange={(e) => setSender({ ...sender, email: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Iqama / National ID *</label>
                    <input
                      type="text"
                      placeholder="10-digit ID"
                      required
                      value={sender.iqamaNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 10) setSender({ ...sender, iqamaNumber: val });
                      }}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800 font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#fff] border border-[#D8D8D8] rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3 mb-4">
                  <div className="p-2 bg-blue-50 text-blue-700 rounded-lg">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-[#828282] font-semibold text-[12px] uppercase tracking-wide">RECEIVER INFORMATION</h2>
                    <p className="text-xs text-slate-500">Consignee contact & route short address</p>
                  </div>
                </div>

                <div className="mb-4">
                  <SelectInput
                    name="receiver.country"
                    label="Country *"
                    placeholder="Select Country"
                    selectOptions={Array.isArray(countries) ? countries.map((country: any) => ({
                      id: country.id,
                      value: country.id,
                      text: country.name,
                    })) : []}
                    value={receiver.country}
                    setValue={(val) => {
                      setReceiver({
                        ...receiver,
                        country: val,
                        city: '',
                        district: ''
                      });
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Full Name *</label>
                    <input
                      type="text"
                      placeholder="Sarah Mohammed"
                      required
                      value={receiver.name}
                      onChange={(e) => setReceiver({ ...receiver, name: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Country *</label>
                    <select
                      value={receiver.country}
                      onChange={(e) => {
                        const val = e.target.value;
                        const selectedCountry = Array.isArray(countries) ? countries.find((c: any) => c.id === val) : null;
                        setReceiver({
                          ...receiver,
                          country: val,
                          dialCode: selectedCountry?.dialCode || '966',
                          city: '',
                          district: ''
                        });
                      }}
                      className="w-full p-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-[#FBD037] outline-none"
                    >
                      <option value="">Select Country</option>
                      {Array.isArray(countries) && countries.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Mobile Number *</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-4 rounded-l-full border border-r-0 border-[#D8D8D8] bg-gray-50 text-gray-500 text-sm">
                        +{receiver.dialCode}
                      </span>
                      <input
                        type="text"
                        placeholder="53 112 2233"
                        required
                        value={receiver.mobile}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val.length <= 12) setReceiver({ ...receiver, mobile: val });
                        }}
                        className="w-full rounded-r-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Iqama / National ID (Recipient) *</label>
                    <input
                      type="text"
                      placeholder="10-digit ID"
                      required
                      value={receiver.iqamaNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val.length <= 10) setReceiver({ ...receiver, iqamaNumber: val });
                      }}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Email Address</label>
                    <input
                      type="email"
                      placeholder="sarah.m@example.sa"
                      value={receiver.email}
                      onChange={(e) => setReceiver({ ...receiver, email: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-4 pb-3 mb-4 border-b border-slate-100">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address Method</span>
                    <button
                      type="button"
                      onClick={() => {
                        const nextType = receiver.addressType === 'short_address' ? 'manual' : 'short_address';
                        setReceiver({
                          ...receiver,
                          addressType: nextType,
                          shortAddress: '',
                          city: '',
                          district: '',
                          address: ''
                        });
                      }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-300 hover:border-slate-400 bg-white transition-all cursor-pointer shadow-xs select-none"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                        receiver.addressType === 'manual' ? 'bg-[#FBD037] shadow-[0_0_8px_#FBD037]' : 'bg-slate-400'
                      }`} />
                      <span className="text-xs font-bold text-slate-700">
                        {receiver.addressType === 'manual' ? 'Manual Address Entry' : 'National Address'}
                      </span>
                    </button>
                  </div>

                  {receiver.addressType === 'short_address' ? (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter short address code (e.g. RIY-YASM-8821)"
                        value={receiver.shortAddress}
                        onChange={(e) => {
                          setReceiver({ ...receiver, shortAddress: e.target.value });
                          setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        className="w-full p-3 text-sm rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#FBD037] outline-none font-mono"
                      />

                      {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                          {suggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              className="p-3 hover:bg-gray-50 cursor-pointer flex items-start"
                              onMouseDown={() => updateFormFields(suggestion)}
                            >
                              <FaMapMarkerAlt className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                              <div className="flex-1">
                                <div className="font-medium text-[#404040]">
                                  {suggestion.shortAddress}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {suggestion.nationalAddress.buildingNumber}{" "}
                                  {suggestion.nationalAddress.street}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {suggestion.nationalAddress.district},{" "}
                                  {suggestion.nationalAddress.city}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedAddress && receiver.shortAddress === selectedAddress.shortAddress && (
                        <div className="p-4 mt-3 bg-slate-50 rounded-xl border border-slate-200">
                          <div className="flex items-start gap-3">
                            <FaMapMarkerAlt className="text-emerald-500 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-sm font-bold text-slate-800">Resolved Location:</p>
                                {(selectedAddress.location?.googleMapsLink || selectedAddress.googleMapsLink) && (
                                  <a
                                    href={selectedAddress.location?.googleMapsLink || selectedAddress.googleMapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 text-xs flex items-center font-semibold hover:underline"
                                  >
                                    View on Map <FaExternalLinkAlt className="ml-1" />
                                  </a>
                                )}
                              </div>
                              <p className="text-sm text-slate-700">
                                {selectedAddress.nationalAddress.buildingNumber}{" "}
                                {selectedAddress.nationalAddress.street}
                                {selectedAddress.nationalAddress.secondaryNumber &&
                                  ` (${selectedAddress.nationalAddress.secondaryNumber})`}
                              </p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs text-slate-500 bg-white p-3 border border-slate-100 rounded-lg">
                                <div><span className="font-semibold text-slate-600">District:</span> {selectedAddress.nationalAddress.district}</div>
                                <div><span className="font-semibold text-slate-600">City:</span> {selectedAddress.nationalAddress.city}</div>
                                <div><span className="font-semibold text-slate-600">Zip:</span> {selectedAddress.nationalAddress.postalCode}</div>
                                {selectedAddress.location && (
                                  <div>
                                    <span className="font-semibold text-slate-600">Coords:</span>{" "}
                                    {selectedAddress.location.latitude.toFixed(4)}, {selectedAddress.location.longitude.toFixed(4)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {receiver.shortAddress && suggestions.length === 0 && (
                        <div className="text-sm text-gray-500 p-2 mt-2 bg-gray-50 rounded-lg">
                          No matching addresses found for "{receiver.shortAddress}"
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SelectInput
                          name="receiver.city"
                          label="City *"
                          placeholder="Select City"
                          selectOptions={cities?.map((c: any) => ({
                            id: c.id,
                            value: c.id,
                            text: c.name,
                          })) || []}
                          value={receiver.city}
                          setValue={(val) => {
                            setReceiver({ ...receiver, city: val, district: '' });
                            if (val) fetchReceiverDistricts();
                          }}
                          onSearch={handleCitySearch}
                          isPending={isCitiesFetchPending}
                        />

                        <SelectInput
                          name="receiver.district"
                          label="District *"
                          placeholder="Select District"
                          selectOptions={districts?.map((d: any) => ({
                            id: d.id,
                            value: d.id,
                            text: d.name,
                          })) || []}
                          value={receiver.district}
                          setValue={(val) => setReceiver({ ...receiver, district: val })}
                          onSearch={handleDistrictSearch}
                          isPending={isDistrictFetchPending}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Zip Code</label>
                          <input
                            type="text"
                            value={receiver.zipCode}
                            onChange={(e) => setReceiver({ ...receiver, zipCode: e.target.value })}
                            className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                          />
                        </div>
                        <div>
                          <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Latitude</label>
                          <input
                            type="text"
                            value={receiver.latitude}
                            onChange={(e) => setReceiver({ ...receiver, latitude: e.target.value })}
                            className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                          />
                        </div>
                        <div>
                          <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Longitude</label>
                          <input
                            type="text"
                            value={receiver.longitude}
                            onChange={(e) => setReceiver({ ...receiver, longitude: e.target.value })}
                            className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Detailed Address *</label>
                        <input
                          type="text"
                          value={receiver.address}
                          onChange={(e) => setReceiver({ ...receiver, address: e.target.value })}
                          className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#fff] border border-[#D8D8D8] rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                  <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg">
                    <Package className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-[#828282] font-semibold text-[12px] uppercase tracking-wide">SHIPMENT DETAILS</h2>
                    <p className="text-xs text-slate-500">Weight metrics & cargo specs</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Customer Reference *</label>
                    <input
                      type="text"
                      placeholder="INV-92812"
                      required
                      value={shipment.reference}
                      onChange={(e) => setShipment({ ...shipment, reference: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Shipment Type *</label>
                    <select
                      value={shipment.shipmentType}
                      onChange={(e) => setShipment({ ...shipment, shipmentType: e.target.value })}
                      className="w-full p-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-[#FBD037] outline-none"
                    >
                      {ORDER_SHIPMENT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.value}>{opt.text}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Total Weight (kg) *</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="e.g. 4.5"
                      required
                      value={shipment.weight}
                      onChange={(e) => setShipment({ ...shipment, weight: e.target.value })}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Number of Pieces</label>
                    <input
                      type="text"
                      required
                      value={shipment.pieces}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setShipment({ ...shipment, pieces: val });
                      }}
                      className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Service Tier</label>
                    <select
                      value={shipment.service}
                      onChange={(e) => setShipment({ ...shipment, service: e.target.value })}
                      className="w-full p-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-[#FBD037] outline-none"
                    >
                      <option value="">Select Service</option>
                      {servicesData?.map((svc: any) => (
                        <option key={svc.id} value={svc.name}>{svc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[#1E293B] text-sm whitespace-nowrap block mb-1">Manifest Description</label>
                  <textarea
                    rows={2}
                    placeholder="Describe contents (e.g., Electronics, Documents, Clothing)"
                    value={shipment.description}
                    onChange={(e) => setShipment({ ...shipment, description: e.target.value })}
                    className="w-full rounded-full py-2 px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-slate-800"
                  />
                </div>
              </div>

            </form>

            <div className="space-y-6">

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                <div className="bg-slate-900 text-white p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-black text-sm tracking-wide">LIVE BILLING PREVIEW</h3>
                    <span className="bg-[#FBD037] text-slate-950 text-[10px] font-bold px-2 py-0.5 rounded uppercase">SAR (Incl. 15% VAT)</span>
                  </div>
                </div>

                <div className="p-5 space-y-4 text-sm">

                  <div className="border-b border-slate-100 pb-3">
                    <span className="text-[10px] font-bold text-slate-400 block uppercase">Sender</span>
                    <p className="font-bold text-slate-900 truncate">{sender.name || '—'}</p>
                    <p className="text-xs text-slate-500">{sender.mobile || 'No Mobile Entered'}</p>
                  </div>

                  <div className="border-b border-slate-100 pb-3">
                    <span className="text-[10px] font-bold text-slate-400 block uppercase">Receiver</span>
                    <p className="font-bold text-slate-900 truncate">{receiver.name || '—'}</p>
                    <p className="text-xs text-slate-500">
                      {receiver.shortAddress ? `SPL: ${receiver.shortAddress}` : 'No Short Address Specified'}
                    </p>
                  </div>

                  <div className="space-y-1.5 pt-2">
                    <div className="flex justify-between items-center text-slate-500 text-xs">
                      <span>Base Rate</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-medium">SAR</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={shipment.cost}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val.startsWith('-')) return;
                            const parsed = parseFloat(val);
                            if (!isNaN(parsed) && parsed < 0) return;
                            setShipment({ ...shipment, cost: val as any });
                          }}
                          className="w-20 text-right border border-slate-200 focus:border-slate-400 rounded-md px-1.5 py-0.5 text-xs outline-none bg-slate-50 hover:bg-white text-slate-900 font-semibold"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-slate-500 text-xs">
                      <span>15% SPL VAT Tax</span>
                      <span>SAR {(baseCost * 0.15).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-900 font-bold border-t border-slate-100 pt-2 text-base">
                      <span>Grand Total</span>
                      <span className="text-emerald-700">SAR {(baseCost * 1.15).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mt-4 space-y-3">
                    <span className="text-xs font-bold text-slate-700 block uppercase">Payment Collection</span>

                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-1.5 p-2 bg-white rounded border border-slate-200 cursor-pointer text-xs">
                        <input
                          type="radio"
                          name="payMethod"
                          checked={shipment.paymentMethod === 'COD'}
                          onChange={() => setShipment({ ...shipment, paymentMethod: 'COD' })}
                        />
                        <span>Cash</span>
                      </label>
                      <label className="flex items-center gap-1.5 p-2 bg-white rounded border border-slate-200 cursor-pointer text-xs">
                        <input
                          type="radio"
                          name="payMethod"
                          checked={shipment.paymentMethod === 'CC'}
                          onChange={() => setShipment({ ...shipment, paymentMethod: 'CC' })}
                        />
                        <span>Mada / Card</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Enter Collected Amount (SAR)</label>
                      <input
                        type="text"
                        placeholder={`SAR ${(baseCost * 1.15).toFixed(2)}`}
                        value={shipment.collectedAmount}
                        onChange={(e) => setShipment({ ...shipment, collectedAmount: e.target.value })}
                        className="w-full p-2 bg-white rounded border border-slate-200 text-sm font-bold text-emerald-800"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handlePreviewAndCollect}
                    disabled={isPending}
                    className="w-full bg-[#FBD037] hover:bg-yellow-400 text-slate-950 font-black py-3.5 rounded-xl text-center shadow-lg transition duration-150 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Check className="h-5 w-5" />
                    {isPending ? "PROCESSING..." : "PREVIEW & COLLECT DISPATCH"}
                  </button>

                  <div className="flex gap-2">
                    <button

                      type="button"
                      onClick={handleResetForm}
                      className="flex-1 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs py-2 rounded-lg font-semibold transition"
                    >
                      Reset Form
                    </button>
                  </div>

                </div>
              </div>

            </div>

          </div>
        )}

        {activeTab === 'directory' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 p-5 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Outlet Dispatch Directory</h3>
                <p className="text-sm text-slate-500">Track and manage counter generated airway bills</p>
              </div>
              <span className="bg-[#FBD037]/20 text-yellow-800 text-xs font-bold px-3 py-1 rounded-full">
                Total Orders: {ordersDirectory.length}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-bold text-xs uppercase">
                    <th className="p-4">AWB / Ref</th>
                    <th className="p-4">Sender (Shipper)</th>
                    <th className="p-4">Receiver (Consignee)</th>
                    <th className="p-4">Destination</th>
                    <th className="p-4">Specs</th>
                    <th className="p-4">Final Fee</th>
                    <th className="p-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ordersDirectory.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-500 font-semibold">
                        No orders generated yet.
                      </td>
                    </tr>
                  )}
                  {ordersDirectory.map((order: any, idx: number) => (
                    <tr key={idx} className="hover:bg-slate-50/80 transition duration-150">
                      <td className="p-4">
                        <div className="font-black text-slate-900">{order.id}</div>
                        <div className="text-[11px] text-slate-500">Ref: {order.shipment.reference}</div>
                        <div className="text-[11px] text-slate-400">{order.date}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-slate-800">{order.sender.name}</div>
                        <div className="text-xs text-slate-500">{order.sender.mobile}</div>
                        <div className="text-xs text-slate-400 font-mono">ID: {order.sender.iqama}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-slate-800">{order.receiver.name}</div>
                        <div className="text-xs text-slate-500">{order.receiver.mobile}</div>
                        <div className="text-xs text-slate-400 font-mono">ID: {order.receiver.iqama || '—'}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-slate-700">{order.receiver.country}</div>
                        <span className="inline-block bg-slate-100 border border-slate-200 text-slate-800 font-mono text-[10px] px-1.5 py-0.5 rounded mt-1">
                          {order.receiver.shortAddress}
                        </span>
                      </td>
                      <td className="p-4 text-slate-600">
                        <div>{order.shipment.type}</div>
                        <div className="text-xs font-semibold text-slate-500">{order.shipment.weight} kg • {order.shipment.pieces} Pcs</div>
                      </td>
                      <td className="p-4 font-bold text-emerald-700">
                        SAR {(order.shipment.cost * 1.15).toFixed(2)}
                        <div className="text-[10px] text-slate-400 font-normal">Incl. VAT</div>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => setSelectedOrderPreview(order)}
                          className="bg-[#FBD037]/20 hover:bg-[#FBD037]/40 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-lg inline-flex items-center gap-1 transition"
                        >
                          <Eye className="h-3 w-3" />
                          View AWB
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {selectedOrderPreview && (
        <div className="fixed inset-0 bg-slate-950/75 flex items-center justify-center p-4 z-50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl flex flex-col">

            <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-[#FBD037]" />
                <span className="font-bold text-sm tracking-wide">THERMAL LABEL & COLLECTION CONFIRMATION</span>
              </div>
              <button
                onClick={() => navigate('/orders')}
                className="text-slate-400 hover:text-white font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-6 bg-slate-50 overflow-y-auto max-h-[70vh] space-y-6">

              <div className="bg-white text-slate-950 p-5 rounded-xl font-mono text-xs border border-slate-300 shadow-sm space-y-4">

                <div className="flex justify-between items-center border-b-2 border-slate-900 pb-2.5">
                  <div>
                    <span className="font-black text-sm tracking-tighter">SAUDI OUTLET EXPRESS</span>
                    <div className="text-[9px] text-slate-500">SPL SHORT ROUTE NETWORK</div>
                  </div>
                  <span className="bg-slate-900 text-white font-black px-2 py-0.5 rounded text-xs">KSA</span>
                </div>

                <div className="space-y-1">
                  <div className="font-bold text-slate-500 text-[10px]">SENDER (SHIPPER):</div>
                  <div className="font-bold text-slate-900 truncate">{selectedOrderPreview.sender.name}</div>
                  <div>ID / Iqama: <span className="font-semibold">{selectedOrderPreview.sender.iqama}</span></div>
                  <div>Mob: {selectedOrderPreview.sender.mobile}</div>
                </div>

                <div className="border-t border-dashed border-slate-300 my-1" />

                <div className="space-y-1">
                  <div className="font-bold text-slate-500 text-[10px]">RECEIVER (CONSIGNEE):</div>
                  <div className="font-bold text-slate-950 text-sm truncate">{selectedOrderPreview.receiver.name}</div>
                  <div>Country: <span className="font-bold">{selectedOrderPreview.receiver.country}</span></div>
                  <div>Mob: {selectedOrderPreview.receiver.mobile}</div>
                  <div>Iqama: {selectedOrderPreview.receiver.iqama || 'Not Provided'}</div>
                </div>

                <div className="border-t border-slate-900 pt-2 flex justify-between items-end bg-slate-100 p-2 rounded">
                  <div>
                    <div className="font-bold text-slate-800 text-[11px]">SPL SHORT ROUTE ADDRESS:</div>
                    <div className="font-black text-sm text-slate-950 tracking-wide">{selectedOrderPreview.receiver.shortAddress}</div>
                  </div>
                </div>

                <div className="border-t border-slate-300 pt-3 flex justify-between items-end">
                  <div className="space-y-1 text-[10px]">
                    <div>AWB: <span className="font-bold text-slate-900">{selectedOrderPreview.id}</span></div>
                    <div>REF: <span className="font-semibold">{selectedOrderPreview.shipment.reference}</span></div>
                    <div>WEIGHT: <span className="font-bold">{selectedOrderPreview.shipment.weight} KG</span></div>
                    <div>PIECES: <span className="font-bold">{selectedOrderPreview.shipment.pieces} Unit(s)</span></div>
                  </div>

                  <div className="bg-slate-950 text-white text-[8px] px-1 py-1 font-sans text-center rounded space-y-0.5 w-24">
                    <div className="h-7 bg-[repeating-linear-gradient(90deg,#fff,#fff_2px,#000_2px,#000_4px)] w-full" />
                    <span>*{selectedOrderPreview.id}*</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 space-y-2">
                <span className="text-xs font-bold text-emerald-800 uppercase block tracking-wider">Payment Confirmed</span>
                <div className="flex justify-between text-sm text-emerald-950">
                  <span>Collected Fee:</span>
                  <span className="font-black">SAR {(selectedOrderPreview.shipment.cost * 1.15).toFixed(2)}</span>
                </div>
              </div>

            </div>

            <div className="bg-slate-100 p-4 border-t border-slate-200 flex gap-2">
              <button
                onClick={() => {
                  triggerToast('Thermal sticker dispatched to counter spool!');
                }}
                className="flex-1 bg-[#FBD037] hover:bg-yellow-400 text-slate-950 font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print Thermal Sticker
              </button>
              <button
                onClick={() => navigate('/orders')}
                className="bg-slate-800 hover:bg-slate-900 text-white font-medium px-4 py-2.5 rounded-xl text-sm"
              >
                Dismiss & Go to Orders
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}





